import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Dimensions,
  ScrollView,
  TextInput,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { Modal } from '../components/Modal';
import { CalendarMonthView } from '../components/calendar';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { MediaService } from '../services/media.service';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const CalendarBookingScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const getUrlParam = (key: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get(key);
    }
    return routeParams?.[key] || null;
  };
  
  const itemId = getUrlParam('itemId');
  const businessId = getUrlParam('businessId') || currentBusiness?._id;
  const contactId = getUrlParam('contactId');
  const phoneNumber = getUrlParam('phoneNumber');
  const calendarId = getUrlParam('calendarId');
  // Present when this page was opened from a Flow's create_calendar_event 'browser' mode
  // (see FlowEngineService.sendBrowserBookingMessage) — identifies which paused FlowSession
  // to resume once booking completes, instead of this page's own ad-hoc confirmation.
  const flowSessionId = getUrlParam('flowSessionId');
  // Flow-driven title override (a Flow node has no quick-reply item to source a title from).
  const flowTitle = getUrlParam('title');
  const isFromWhatsApp = !!(contactId && phoneNumber);
  
  const [loading, setLoading] = useState(true);
  const [calendarConfig, setCalendarConfig] = useState<any>(null);
  const [calendar, setCalendar] = useState<any>(null); // Calendar with availability settings
  const [existingEvents, setExistingEvents] = useState<any[]>([]);
  const [viewDate, setViewDate] = useState(new Date());
  
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);
  
  const [selectedDay, setSelectedDay] = useState<Date | null>(
    isMobileBrowser || Platform.OS !== 'web' ? new Date() : null
  );
  const [viewMode, setViewMode] = useState<'month' | 'day'>(
    isMobileBrowser || Platform.OS !== 'web' ? 'day' : 'month'
  ); // Force day view on mobile browser
  
  // Floating calendar picker state
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [calendarPickerDate, setCalendarPickerDate] = useState(new Date());
  
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedHour, setSelectedHour] = useState(9); // 1-12 format
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedAmPm, setSelectedAmPm] = useState<'AM' | 'PM'>('AM');
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState('');
  const [joinUrl, setJoinUrl] = useState('');
  const [attachments, setAttachments] = useState<Array<{ uri: string; name: string; type: string }>>([]);
  const [booking, setBooking] = useState(false);
  const [calendarsMap, setCalendarsMap] = useState<Map<string, any>>(new Map());
  const [selectedEventForEdit, setSelectedEventForEdit] = useState<any | null>(null);
  const [showEventEditModal, setShowEventEditModal] = useState(false);
  const [showEventSelectModal, setShowEventSelectModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [countdown, setCountdown] = useState(10);
  
  useEffect(() => {
    console.log('[CalendarBooking] useEffect triggered:', {
      businessId,
      phoneNumber,
      calendarId,
      itemId,
    });
    loadCalendarConfig();
    if (businessId) {
      if (phoneNumber) {
        console.log('[CalendarBooking] Loading events by phone number');
        loadExistingEvents();
      } else if (calendarId) {
        // If we have calendarId but no phoneNumber, load events by calendarId
        console.log('[CalendarBooking] Loading events by calendarId');
        loadEventsByCalendar();
      } else {
        console.log('[CalendarBooking] No phoneNumber or calendarId, skipping event load');
      }
    } else {
      console.log('[CalendarBooking] No businessId, skipping event load');
    }
  }, [itemId, businessId, phoneNumber, calendarId]);
  
  const loadCalendarConfig = async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    
    try {
      const { apiService } = await import('../services/api');
      const { getCurrentApiConfig } = await import('../config/api.config');
      const apiConfig = getCurrentApiConfig();
      
      // If calendarId is in query string, load it directly using ApiService
      if (calendarId) {
        try {
          console.log('[CalendarBooking] Loading calendar from query string:', calendarId, 'businessId:', businessId);
          const calendarResponse = await apiService.getCalendarById(calendarId, businessId);
          console.log('[CalendarBooking] Calendar API response:', {
            statusCode: calendarResponse.statusCode,
            hasData: !!calendarResponse.data,
            data: calendarResponse.data,
          });
          if (calendarResponse.statusCode === 1000 && calendarResponse.data) {
            console.log('[CalendarBooking] Calendar loaded with availability:', {
              id: calendarResponse.data._id,
              name: calendarResponse.data.name,
              availability: calendarResponse.data.availability,
              availabilityMode: calendarResponse.data.availability?.mode,
              availabilityDaysOfWeek: calendarResponse.data.availability?.daysOfWeek,
              availabilityTimeRange: calendarResponse.data.availability?.timeRange,
              isAllDay: calendarResponse.data.availability?.isAllDay,
            });
            setCalendar(calendarResponse.data);
            // Add calendar to calendarsMap for color reference
            setCalendarsMap(prev => {
              const newMap = new Map(prev);
              newMap.set(calendarResponse.data._id, calendarResponse.data);
              return newMap;
            });
          } else {
            console.error('[CalendarBooking] Failed to load calendar:', calendarResponse);
          }
        } catch (error: any) {
          console.error('Failed to load calendar from query string:', error);
        }
      } else {
        console.log('[CalendarBooking] No calendarId in query string');
      }
      
      // Load item config if itemId is provided
      if (itemId) {
        const response = await fetch(`${apiConfig.baseURL}/api/omni-channel/calendar/item-config/${itemId}?businessId=${businessId}`);
        const data = await response.json();
        
        if (data.statusCode === 1000 && data.data) {
          setCalendarConfig(data.data);
          if (data.data.durationMinutes) setDuration(data.data.durationMinutes);
          
          // Load calendar details if calendarId is in item config (and not already loaded from query string)
          if (data.data.calendarId && !calendarId) {
            try {
              console.log('[CalendarBooking] Loading calendar from item config:', data.data.calendarId);
              const calendarResponse = await apiService.getCalendarById(data.data.calendarId, businessId);
              if (calendarResponse.statusCode === 1000 && calendarResponse.data) {
                console.log('[CalendarBooking] Calendar loaded with availability:', {
                  id: calendarResponse.data._id,
                  name: calendarResponse.data.name,
                  availability: calendarResponse.data.availability,
                });
                setCalendar(calendarResponse.data);
                // Add calendar to calendarsMap for color reference
                setCalendarsMap(prev => {
                  const newMap = new Map(prev);
                  newMap.set(calendarResponse.data._id, calendarResponse.data);
                  return newMap;
                });
              }
            } catch (error: any) {
              console.error('Failed to load calendar from item config:', error);
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadExistingEvents = async () => {
    if (!phoneNumber || !businessId) {
      console.log('[CalendarBooking] loadExistingEvents: Missing phoneNumber or businessId', { phoneNumber, businessId });
      return;
    }
    
    try {
      console.log('[CalendarBooking] loadExistingEvents: Loading events for phoneNumber:', phoneNumber);
      const { getCurrentApiConfig } = await import('../config/api.config');
      const apiConfig = getCurrentApiConfig();
      // Include calendarId in the query if provided to filter events by calendar
      const calendarIdParam = calendarId ? `&calendarId=${encodeURIComponent(calendarId)}` : '';
      const url = `${apiConfig.baseURL}/api/omni-channel/calendar/events/by-phone?businessId=${businessId}&phoneNumber=${encodeURIComponent(phoneNumber)}${calendarIdParam}`;
      console.log('[CalendarBooking] loadExistingEvents: Fetching from URL:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('[CalendarBooking] loadExistingEvents: API response:', {
        statusCode: data.statusCode,
        eventCount: data.data?.length || 0,
      });
      
      if (data.statusCode === 1000 && data.data) {
        const events = data.data || [];
        // If calendarId is provided, filter events to only show events from that calendar
        const filteredEvents = calendarId 
          ? events.filter((e: any) => e.calendarId === calendarId)
          : events;
        console.log('[CalendarBooking] loadExistingEvents: Setting events:', filteredEvents.length);
        setExistingEvents(filteredEvents);
        
        // Load calendar information for each unique calendarId
        const uniqueCalendarIds = [...new Set(events.map((e: any) => e.calendarId).filter(Boolean))];
        const calendars = new Map<string, any>();
        
        for (const calendarId of uniqueCalendarIds) {
          try {
            const { apiService } = await import('../services/api');
            const calendarResponse = await apiService.getCalendarById(calendarId, businessId);
            if (calendarResponse.statusCode === 1000 && calendarResponse.data) {
              calendars.set(calendarId, calendarResponse.data);
            }
          } catch (error: any) {
            console.error(`Failed to load calendar ${calendarId}:`, error);
          }
        }
        
        setCalendarsMap(calendars);
      }
    } catch (error: any) {
      console.error('[CalendarBooking] Failed to load events:', error);
    }
  };
  
  // Load events by calendarId when phoneNumber is not available
  const loadEventsByCalendar = async () => {
    if (!calendarId || !businessId) {
      console.log('[CalendarBooking] loadEventsByCalendar: Missing calendarId or businessId', { calendarId, businessId });
      return;
    }
    
    try {
      console.log('[CalendarBooking] loadEventsByCalendar: Loading events for calendarId:', calendarId);
      const { apiService } = await import('../services/api');
      const startDate = new Date(new Date().setMonth(new Date().getMonth() - 1)); // Get events from last month
      const endDate = new Date(new Date().setMonth(new Date().getMonth() + 2)); // Get events up to 2 months ahead
      
      console.log('[CalendarBooking] loadEventsByCalendar: Date range:', { startDate, endDate });
      
      const response = await apiService.getEvents({
        businessId,
        calendarId,
        startDate,
        endDate,
      });
      
      console.log('[CalendarBooking] loadEventsByCalendar: API response:', {
        statusCode: response.statusCode,
        eventCount: response.data?.length || 0,
      });
      
      if (response.statusCode === 1000 && response.data) {
        const events = response.data || [];
        console.log('[CalendarBooking] loadEventsByCalendar: Setting events:', events.length);
        setExistingEvents(events);
        
        // Load calendar information
        const calendarResponse = await apiService.getCalendarById(calendarId, businessId);
        if (calendarResponse.statusCode === 1000 && calendarResponse.data) {
          const calendars = new Map<string, any>();
          calendars.set(calendarId, calendarResponse.data);
          setCalendarsMap(calendars);
        }
      }
    } catch (error: any) {
      console.error('[CalendarBooking] Failed to load events by calendar:', error);
    }
  };
  
  const getDateEvents = useCallback((date: Date) => {
    const dateStr = date.toDateString();
    return existingEvents.filter(e => new Date(e.startDate).toDateString() === dateStr);
  }, [existingEvents]);

  // Check if a date is available based on calendar availability settings
  const isDateAvailable = useCallback((date: Date): boolean => {
    // If no calendar or no availability settings, allow all dates
    if (!calendar || !calendar.availability) {
      console.log('[CalendarBooking] isDateAvailable: No calendar or availability, allowing date:', date.toDateString());
      return true;
    }
    
    const availability = calendar.availability;
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    
    console.log('[CalendarBooking] isDateAvailable check:', {
      date: date.toDateString(),
      dayOfWeek,
      mode: availability.mode,
      daysOfWeek: availability.daysOfWeek,
      startDate: availability.startDate,
      endDate: availability.endDate,
    });
    
    // Check mode-specific restrictions
    if (availability.mode === 'all_time') {
      // All dates are available
      return true;
    } else if (availability.mode === 'specific_period') {
      // Check if date is within the period
      if (availability.startDate && availability.endDate) {
        const startDate = new Date(availability.startDate);
        const endDate = new Date(availability.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        const isInRange = dateOnly >= startDate && dateOnly <= endDate;
        console.log('[CalendarBooking] Date range check:', {
          dateOnly: dateOnly.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          isInRange,
        });
        if (!isInRange) {
          return false;
        }
      }
      return true;
    } else if (availability.mode === 'customized_days') {
      // Check if day of week is in the allowed days
      if (availability.daysOfWeek && availability.daysOfWeek.length > 0) {
        const isDayAllowed = availability.daysOfWeek.includes(dayOfWeek);
        console.log('[CalendarBooking] Day of week check:', {
          dayOfWeek,
          allowedDays: availability.daysOfWeek,
          isDayAllowed,
        });
        if (!isDayAllowed) {
          return false;
        }
      }
      return true;
    }
    
    return true;
  }, [calendar]);

  // Generate time slots for a given day based on calendar availability and duration
  const generateTimeSlots = useCallback((date: Date): Array<{ startTime: Date; endTime: Date; isAvailable: boolean; event?: any }> => {
    if (!calendar || !calendar.availability) {
      // Default: 9 AM to 5 PM, 60-minute slots
      const slots: Array<{ startTime: Date; endTime: Date; isAvailable: boolean; event?: any }> = [];
      const startHour = 9;
      const endHour = 17;
      
      for (let hour = startHour; hour < endHour; hour++) {
        const startTime = new Date(date);
        startTime.setHours(hour, 0, 0, 0);
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + duration);
        
        // Check if slot conflicts with existing events
        const conflictingEvent = existingEvents.find((e: any) => {
          const eventStart = new Date(e.startDate);
          const eventEnd = new Date(e.endDate);
          return (startTime < eventEnd && endTime > eventStart);
        });
        
        slots.push({
          startTime,
          endTime,
          isAvailable: !conflictingEvent,
          event: conflictingEvent,
        });
      }
      return slots;
    }

    const availability = calendar.availability;
    const slots: Array<{ startTime: Date; endTime: Date; isAvailable: boolean; event?: any }> = [];

    // Check if date is available
    if (!isDateAvailable(date)) {
      return slots; // Return empty slots if date is not available
    }

    // Get time range
    let startHour = 9;
    let startMinute = 0;
    let endHour = 17;
    let endMinute = 0;

    if (!availability.isAllDay && availability.timeRange) {
      const [startH, startM] = availability.timeRange.startTime.split(':').map(Number);
      const [endH, endM] = availability.timeRange.endTime.split(':').map(Number);
      startHour = startH;
      startMinute = startM;
      endHour = endH;
      endMinute = endM;
    }

    // Generate slots based on duration
    const slotStart = new Date(date);
    slotStart.setHours(startHour, startMinute, 0, 0);
    
    const slotEnd = new Date(date);
    slotEnd.setHours(endHour, endMinute, 0, 0);

    let currentTime = new Date(slotStart);
    
    while (currentTime < slotEnd) {
      const slotStartTime = new Date(currentTime);
      const slotEndTime = new Date(currentTime);
      slotEndTime.setMinutes(slotEndTime.getMinutes() + duration);

      // Don't create slots that extend beyond the end time
      if (slotEndTime > slotEnd) {
        break;
      }

      // Check if slot conflicts with existing events
      const conflictingEvent = existingEvents.find((e: any) => {
        const eventStart = new Date(e.startDate);
        const eventEnd = new Date(e.endDate);
        return (slotStartTime < eventEnd && slotEndTime > eventStart);
      });

      slots.push({
        startTime: slotStartTime,
        endTime: slotEndTime,
        isAvailable: !conflictingEvent,
        event: conflictingEvent,
      });

      // Move to next slot (same duration)
      currentTime = new Date(slotEndTime);
    }

    return slots;
  }, [calendar, duration, existingEvents, isDateAvailable]);

  // Get available hours based on calendar availability time range
  const getAvailableHours = useCallback((): { hour: number; ampm: 'AM' | 'PM' }[] => {
    console.log('[CalendarBooking] getAvailableHours called, calendar:', calendar ? {
      id: calendar._id,
      hasAvailability: !!calendar.availability,
      availability: calendar.availability,
    } : 'null');
    
    if (!calendar?.availability) {
      console.log('[CalendarBooking] No calendar availability, returning default hours');
      // No time restrictions, return all hours (9 AM - 5 PM)
      return [
        { hour: 9, ampm: 'AM' },
        { hour: 10, ampm: 'AM' },
        { hour: 11, ampm: 'AM' },
        { hour: 12, ampm: 'PM' },
        { hour: 1, ampm: 'PM' },
        { hour: 2, ampm: 'PM' },
        { hour: 3, ampm: 'PM' },
        { hour: 4, ampm: 'PM' },
        { hour: 5, ampm: 'PM' },
      ];
    }
    
    if (calendar.availability.isAllDay) {
      console.log('[CalendarBooking] isAllDay=true, returning default hours');
      // All day available (9 AM - 5 PM)
      return [
        { hour: 9, ampm: 'AM' },
        { hour: 10, ampm: 'AM' },
        { hour: 11, ampm: 'AM' },
        { hour: 12, ampm: 'PM' },
        { hour: 1, ampm: 'PM' },
        { hour: 2, ampm: 'PM' },
        { hour: 3, ampm: 'PM' },
        { hour: 4, ampm: 'PM' },
        { hour: 5, ampm: 'PM' },
      ];
    }
    
    if (!calendar.availability.timeRange) {
      console.log('[CalendarBooking] No timeRange, returning default hours');
      // No time range specified, return all hours
      return [
        { hour: 9, ampm: 'AM' },
        { hour: 10, ampm: 'AM' },
        { hour: 11, ampm: 'AM' },
        { hour: 12, ampm: 'PM' },
        { hour: 1, ampm: 'PM' },
        { hour: 2, ampm: 'PM' },
        { hour: 3, ampm: 'PM' },
        { hour: 4, ampm: 'PM' },
        { hour: 5, ampm: 'PM' },
      ];
    }
    
    const timeRange = calendar.availability.timeRange;
    console.log('[CalendarBooking] Using timeRange:', timeRange);
    const [startHour, startMinute] = timeRange.startTime.split(':').map(Number);
    const [endHour, endMinute] = timeRange.endTime.split(':').map(Number);
    
    console.log('[CalendarBooking] Parsed time range:', {
      startHour,
      startMinute,
      endHour,
      endMinute,
    });
    
    // Convert to 12-hour format hours array with AM/PM
    const availableHours: { hour: number; ampm: 'AM' | 'PM' }[] = [];
    
    // Generate hours from start to end (exclusive of end)
    for (let hour24 = startHour; hour24 < endHour; hour24++) {
      let hour12: number;
      let ampm: 'AM' | 'PM';
      
      if (hour24 === 0) {
        hour12 = 12;
        ampm = 'AM';
      } else if (hour24 === 12) {
        hour12 = 12;
        ampm = 'PM';
      } else if (hour24 < 12) {
        hour12 = hour24;
        ampm = 'AM';
      } else {
        hour12 = hour24 - 12;
        ampm = 'PM';
      }
      
      availableHours.push({ hour: hour12, ampm });
    }
    
    console.log('[CalendarBooking] Generated available hours:', availableHours);
    
    // Remove duplicates and sort
    const uniqueHours = availableHours.filter((item, index, self) =>
      index === self.findIndex((t) => t.hour === item.hour && t.ampm === item.ampm)
    );
    
    // Sort: AM hours first (9, 10, 11, 12), then PM hours (12, 1, 2, 3, 4, 5)
    const sorted = uniqueHours.sort((a, b) => {
      if (a.ampm !== b.ampm) {
        return a.ampm === 'AM' ? -1 : 1;
      }
      if (a.hour === 12 && b.hour !== 12) {
        return a.ampm === 'AM' ? -1 : 1;
      }
      if (b.hour === 12 && a.hour !== 12) {
        return b.ampm === 'AM' ? 1 : -1;
      }
      return a.hour - b.hour;
    });
    
    console.log('[CalendarBooking] Final sorted available hours:', sorted);
    return sorted;
  }, [calendar]);

  // Ensure selected hour is within available hours when calendar availability changes
  useEffect(() => {
    if (calendar?.availability) {
      const availableHours = getAvailableHours();
      console.log('[CalendarBooking] Available hours from calendar:', availableHours);
      if (availableHours.length > 0) {
        const currentHourMatch = availableHours.find(h => h.hour === selectedHour && h.ampm === selectedAmPm);
        if (!currentHourMatch) {
          // Current hour is not available, set to first available
          const firstHour = availableHours[0];
          console.log('[CalendarBooking] Setting hour to first available:', firstHour);
          setSelectedHour(firstHour.hour);
          setSelectedAmPm(firstHour.ampm);
        }
      }
    }
  }, [calendar, selectedHour, selectedAmPm, getAvailableHours]);
  
  // Debug: Log calendar state changes
  useEffect(() => {
    console.log('[CalendarBooking] Calendar state changed:', {
      hasCalendar: !!calendar,
      calendarId: calendar?._id,
      calendarName: calendar?.name,
      hasAvailability: !!calendar?.availability,
      availability: calendar?.availability,
    });
    
    if (calendar?.availability) {
      console.log('[CalendarBooking] Availability details:', {
        mode: calendar.availability.mode,
        isAllDay: calendar.availability.isAllDay,
        timeRange: calendar.availability.timeRange,
        daysOfWeek: calendar.availability.daysOfWeek,
        startDate: calendar.availability.startDate,
        endDate: calendar.availability.endDate,
      });
    }
  }, [calendar]);
  
  // Calculate maximum day cell height to prevent scrolling
  // Reserve space for: container padding (top+bottom), month nav, week headers, 6 rows max of days, gaps
  const containerTopBottomPadding = spacing.md * 2; // Container padding
  const monthNavHeight = 32;
  const weekHeaderHeight = 24;
  const gapsBetweenRows = 2 * 5; // 2px gaps between 6 rows (5 gaps)
  const reservedVerticalSpace = containerTopBottomPadding + monthNavHeight + weekHeaderHeight + gapsBetweenRows;
  const availableHeight = screenHeight - reservedVerticalSpace - (Platform.OS === 'web' ? 100 : 0); // Extra space for web headers
  const maxRows = 6; // Maximum rows in a month view
  const maxDayCellHeight = Math.floor(availableHeight / maxRows);
  
  // Calculate day cell width based on container width (14.28% = 100% / 7)
  // Container width is 85% of screen width
  const containerWidth = Math.floor(screenWidth * 0.85);
  const containerPadding = spacing.sm * 2;
  const availableWidth = containerWidth - containerPadding;
  const dayCellWidth = Math.floor((availableWidth / 100) * 14.28); // 14.28% of available width
  
  // Use the smaller dimension to ensure cells fit without scrolling
  const maxDaySize = Math.min(dayCellWidth, maxDayCellHeight);
  
  const renderMonthGrid = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysCount = lastDay.getDate();
    const startOffset = firstDay.getDay();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const cells = [];
    
    // Week day headers - Fixed 14.28% width (100% / 7)
    weekDays.forEach((day, i) => {
      cells.push(
        <View key={`h-${i}`} style={styles.weekDayHeader}>
          <Text style={styles.weekDayText}>{day}</Text>
        </View>
      );
    });
    
    // Empty cells - Fixed 14.28% width (100% / 7) with calculated height
    for (let i = 0; i < startOffset; i++) {
      cells.push(
        <View 
          key={`e-${i}`} 
          style={[styles.dayCellEmpty, { height: maxDaySize, maxHeight: maxDaySize }]} 
        />
      );
    }
    
    // Month days - Fixed 14.28% width (100% / 7) with calculated height
    for (let day = 1; day <= daysCount; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const isPast = date < today;
      const isToday = date.getTime() === today.getTime();
      const isSelected = selectedDay?.getTime() === date.getTime();
      const dayEvents = getDateEvents(date);
      const hasEvent = dayEvents.length > 0;
      const isAvailable = isDateAvailable(date);
      const isDisabled = isPast || !isAvailable;
      
      cells.push(
        <TouchableOpacity
          key={`d-${day}`}
          style={[
            styles.dayCell,
            { height: maxDaySize, maxHeight: maxDaySize },
            isToday && styles.dayToday,
            isSelected && styles.daySelected,
            isPast && styles.dayPast,
            !isAvailable && styles.dayCellUnavailable,
            !hasEvent && styles.dayCellNoEvents, // Center when no events
          ]}
          onPress={() => {
            if (!isDisabled) {
              setSelectedDay(new Date(date));
              setShowTimeModal(true);
            }
          }}
          disabled={isDisabled}
          activeOpacity={isDisabled ? 1 : 0.7}
        >
          <View style={[styles.dayCellHeader, !hasEvent && styles.dayCellHeaderCentered]}>
            <Text style={[
              styles.dayNumber,
              isToday && styles.dayNumberToday,
              isSelected && styles.dayNumberSelected,
              isPast && styles.dayNumberPast,
              !isAvailable && styles.dayNumberUnavailable,
            ]}>
              {day}
            </Text>
          </View>
          
          {/* Enhanced event cards inside day cell */}
          {hasEvent && (
            <View style={styles.dayEventsContainer}>
              {dayEvents.slice(0, 2).map((event, idx) => {
                const calendar = event.calendarId ? calendarsMap.get(event.calendarId) : null;
                const calendarColor = calendar?.color || colors.primary;
                
                return (
                  <TouchableOpacity
                    key={event._id || idx}
                    style={[styles.dayEventCard, { borderLeftColor: calendarColor }]}
                    onPress={(e) => {
                      e.stopPropagation(); // Prevent day cell click
                      console.log('Event selected for edit:', JSON.stringify(event, null, 2));
                      console.log('Event attachments:', event.attachments);
                      setSelectedEventForEdit(event);
                      setShowEventEditModal(true);
                    }}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.dayEventTag, { backgroundColor: calendarColor + '20' }]}>
                      <View style={[styles.dayEventTagDot, { backgroundColor: calendarColor }]} />
                    </View>
                    <Text 
                      style={[styles.dayEventTime, { color: calendarColor }]} 
                      numberOfLines={1}
                    >
                      {new Date(event.startDate).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      })}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              {dayEvents.length > 2 && (
                <TouchableOpacity
                  style={styles.dayEventMore}
                  onPress={(e) => {
                    e.stopPropagation(); // Prevent day cell click
                    setSelectedDay(new Date(date));
                    setShowEventSelectModal(true);
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={styles.dayEventMoreText}>
                    +{dayEvents.length - 2}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </TouchableOpacity>
      );
    }
    
    // Calculate max height for grid (6 rows max + week header + gaps)
    const weekHeaderHeight = 24;
    const gapsHeight = 2 * 5; // 5 gaps between 6 rows
    const maxGridHeight = weekHeaderHeight + (maxDaySize * 6) + gapsHeight;
    
    return <View style={[styles.monthGrid, { maxHeight: maxGridHeight }]}>{cells}</View>;
  };

  // Render day view with time slots
  const renderDayView = () => {
    const dayToShow = selectedDay || viewDate;
    const slots = generateTimeSlots(dayToShow);
    
    return (
      <ScrollView style={styles.dayViewContainer} showsVerticalScrollIndicator={false}>
        <View style={styles.dayViewHeader}>
          <TouchableOpacity
            style={[{ flexDirection: rtl.rowDirection, alignItems: 'center', flex: 1 }]}
            onPress={() => {
              setCalendarPickerDate(dayToShow);
              setShowCalendarPicker(true);
            }}
            activeOpacity={0.7}
          >
            <Ionicons name="calendar-outline" size={20} color={colors.primary} style={rtl.marginEnd(spacing.xs)} />
            <Text style={styles.dayViewDate}>
              {dayToShow.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </Text>
          </TouchableOpacity>
          <Text style={styles.dayViewDuration}>
            {t('calendar.booking.durationSlots', { minutes: duration })}
          </Text>
        </View>

        {slots.length === 0 ? (
          <View style={styles.noSlotsContainer}>
            <Ionicons name="calendar-outline" size={48} color={colors.text.secondary} />
            <Text style={styles.noSlotsText}>{t('calendar.booking.noSlotsForDay')}</Text>
          </View>
        ) : (
          <View style={styles.slotsContainer}>
            {slots.map((slot, index) => {
              const calendarColor = slot.event?.calendarId 
                ? (calendarsMap.get(slot.event.calendarId)?.color || colors.primary)
                : colors.primary;
              
              return (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.timeSlot,
                    !slot.isAvailable && styles.timeSlotBooked,
                    slot.event && { borderLeftColor: calendarColor, borderLeftWidth: 4 },
                  ]}
                  onPress={() => {
                    if (slot.isAvailable) {
                      // Set the selected time and open booking modal
                      const startHour = slot.startTime.getHours();
                      const startMinute = slot.startTime.getMinutes();
                      const hour12 = startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour;
                      const ampm = startHour >= 12 ? 'PM' : 'AM';
                      
                      setSelectedDay(dayToShow);
                      setSelectedHour(hour12);
                      setSelectedMinute(startMinute);
                      setSelectedAmPm(ampm);
                      setShowTimeModal(true);
                    } else if (slot.event) {
                      // Open event edit modal
                      setSelectedEventForEdit(slot.event);
                      setShowEventEditModal(true);
                    }
                  }}
                  activeOpacity={0.7}
                  disabled={!slot.isAvailable && !slot.event}
                >
                  <View style={[styles.timeSlotContent, { flexDirection: rtl.rowDirection }]}>
                    <View style={[styles.timeSlotTime, { flexDirection: rtl.rowDirection }]}>
                      <Text style={[
                        styles.timeSlotTimeText,
                        !slot.isAvailable && styles.timeSlotTimeTextBooked,
                      ]}>
                        {slot.startTime.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Text>
                      <Text style={styles.timeSlotTimeSeparator}> - </Text>
                      <Text style={[
                        styles.timeSlotTimeText,
                        !slot.isAvailable && styles.timeSlotTimeTextBooked,
                      ]}>
                        {slot.endTime.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Text>
                    </View>

                    {slot.event ? (
                      <View style={styles.timeSlotEvent}>
                        <View style={[styles.timeSlotEventTag, { backgroundColor: calendarColor + '20', flexDirection: rtl.rowDirection }]}>
                          <View style={[styles.timeSlotEventTagDot, rtl.marginEnd(spacing.xs), { backgroundColor: calendarColor }]} />
                          <Text style={[styles.timeSlotEventTagText, { color: calendarColor }]}>
                            {calendarsMap.get(slot.event.calendarId)?.name || t('calendar.booking.eventFallback')}
                          </Text>
                        </View>
                        <Text style={styles.timeSlotEventTitle} numberOfLines={1}>
                          {slot.event.title || t('calendar.booking.untitledEvent')}
                        </Text>
                        {slot.event.description && (
                          <Text style={styles.timeSlotEventDescription} numberOfLines={2}>
                            {slot.event.description}
                          </Text>
                        )}
                      </View>
                    ) : (
                      <View style={[styles.timeSlotAvailable, { flexDirection: rtl.rowDirection }]}>
                        <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                        <Text style={styles.timeSlotAvailableText}>{t('calendar.booking.available')}</Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </ScrollView>
    );
  };
  
  const selectedStartTime = useMemo(() => {
    if (!selectedDay) return null;
    const time = new Date(selectedDay);
    // Convert 12-hour format to 24-hour format
    let hour24 = selectedHour;
    if (selectedAmPm === 'PM' && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (selectedAmPm === 'AM' && selectedHour === 12) {
      hour24 = 0;
    }
    time.setHours(hour24, selectedMinute, 0, 0);
    return time;
  }, [selectedDay, selectedHour, selectedMinute, selectedAmPm]);
  
  const selectedEndTime = useMemo(() => {
    if (!selectedStartTime) return null;
    return new Date(selectedStartTime.getTime() + duration * 60000);
  }, [selectedStartTime, duration]);
  
  const handleBooking = async () => {
    if (!selectedDay || !selectedStartTime || !selectedEndTime || !businessId) return;
    
    try {
      setBooking(true);
      
      // Convert attachment files to base64
      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          try {
            // Read file as base64
            const response = await fetch(file.uri);
            const blob = await response.blob();
            const reader = new FileReader();
            
            return new Promise<{ uri: string; name: string; type: string }>((resolve, reject) => {
              reader.onloadend = () => {
                const base64 = reader.result as string;
                resolve({
                  uri: base64,
                  name: file.name,
                  type: file.type,
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch (error: any) {
            console.error('Failed to convert file to base64:', error);
            // Return original if conversion fails
            return file;
          }
        })
      );
      
      const { getCurrentApiConfig } = await import('../config/api.config');
      const apiConfig = getCurrentApiConfig();
      const response = await fetch(`${apiConfig.baseURL}/api/omni-channel/calendar/book-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: itemId || undefined,
          businessId,
          contactId,
          phoneNumber,
          calendarId: calendarId || undefined,
          flowSessionId: flowSessionId || undefined,
          startDate: selectedStartTime.toISOString(),
          endDate: selectedEndTime.toISOString(),
          title: calendarConfig?.title || flowTitle || 'Appointment',
          description: notes || calendarConfig?.description,
          location: calendarConfig?.location,
          joinUrl: joinUrl || undefined,
          attachments: attachmentData.length > 0 ? attachmentData : undefined,
          isFromWhatsApp,
        }),
      });
      
      const data = await response.json();
      
      if (data.statusCode === 1000) {
        setShowTimeModal(false);
        setNotes('');
        setJoinUrl('');
        setAttachments([]);
        await loadExistingEvents();
        // Show success modal with countdown
        setShowSuccessModal(true);
        setCountdown(10);
      } else {
        showNotification('error', t('common.error'), data.errors?.[0] || t('calendar.booking.bookingFailedError'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('calendar.booking.bookingFailedError'));
    } finally {
      setBooking(false);
    }
  };
  
  const handleCloseSuccess = () => {
    // Try to close the tab/window directly
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Try to close the window/tab
      // Note: window.close() only works if the window was opened by JavaScript
      // If opened by user directly, browsers block it for security
      try {
        window.close();
        // If window.close() doesn't work immediately, try redirecting to blank page
        setTimeout(() => {
          if (window && !document.hidden) {
            // Fallback: redirect to blank page
            window.location.href = 'about:blank';
          }
        }, 200);
      } catch (error) {
        // If closing fails, redirect to blank page
        window.location.href = 'about:blank';
      }
    } else {
      // For mobile apps, hide modal and navigate to home
      setShowSuccessModal(false);
      navigateTo('home');
    }
  };

  // Handle countdown when success modal is shown
  useEffect(() => {
    if (showSuccessModal && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Close the tab/window when countdown reaches 0
            handleCloseSuccess();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal, countdown]);
  
  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loader}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      {/* Close button */}
      <TouchableOpacity
        style={styles.closeBtn}
        onPress={() => {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.close();
          } else {
            navigateTo('home');
          }
        }}
        activeOpacity={0.7}
      >
        <Ionicons name="close" size={18} color={colors.text.primary} />
      </TouchableOpacity>
      
      {/* Calendar container - centered, Windows-style */}
      <View style={[styles.calendarContainer, { width: Math.floor(screenWidth * 0.85) }]}>
        {/* Month/Day header */}
        <View style={[styles.monthNav, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity
            style={styles.navBtn}
            onPress={() => {
              if (viewMode === 'month') {
                const prev = new Date(viewDate);
                prev.setMonth(prev.getMonth() - 1);
                setViewDate(prev);
              } else {
                const prev = new Date(viewDate);
                prev.setDate(prev.getDate() - 1);
                setViewDate(prev);
                setSelectedDay(prev);
              }
            }}
            activeOpacity={0.7}
          >
            <DirectionalIcon name="chevron-back" size={18} color={colors.text.primary} />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.viewModeToggle, { flexDirection: rtl.rowDirection }]}
            onPress={() => {
              // Don't allow mode switching on mobile browser
              if (isMobileBrowser || Platform.OS !== 'web') {
                setCalendarPickerDate(selectedDay || viewDate);
                setShowCalendarPicker(true);
                return;
              }
              const newMode = viewMode === 'month' ? 'day' : 'month';
              setViewMode(newMode);
              if (newMode === 'day' && !selectedDay) {
                setSelectedDay(viewDate);
              }
            }}
            activeOpacity={0.7}
          >
            <Text style={styles.monthLabel}>
              {viewMode === 'month'
                ? viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                : (selectedDay || viewDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
              }
            </Text>
            <Ionicons
              name={viewMode === 'month' ? 'calendar-outline' : 'grid-outline'}
              size={16}
              color={colors.text.secondary}
              style={rtl.marginStart(spacing.xs)}
            />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.navBtn}
            onPress={() => {
              if (viewMode === 'month') {
                const next = new Date(viewDate);
                next.setMonth(next.getMonth() + 1);
                setViewDate(next);
              } else {
                const next = new Date(viewDate);
                next.setDate(next.getDate() + 1);
                setViewDate(next);
                setSelectedDay(next);
              }
            }}
            activeOpacity={0.7}
          >
            <DirectionalIcon name="chevron-forward" size={18} color={colors.text.primary} />
          </TouchableOpacity>
        </View>
        
        {/* Calendar view - month or day */}
        {viewMode === 'month' ? renderMonthGrid() : renderDayView()}
      </View>
      
        {/* Time selection modal */}
        <Modal
          visible={showTimeModal}
          onClose={() => setShowTimeModal(false)}
          title={selectedDay ? selectedDay.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          }) : t('calendar.booking.selectTimeTitle')}
          customHeader={selectedStartTime && selectedEndTime ? (
            <View style={[styles.eventHeaderDateTime, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="time" size={20} color={colors.white} />
              <View style={[styles.eventHeaderDateTimeText, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.eventHeaderTimeMain}>
                  {selectedStartTime.toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit', hour12: true
                  })}
                </Text>
                <Text style={styles.eventHeaderTimeTo}>{t('calendar.booking.timeRangeTo')}</Text>
                <Text style={styles.eventHeaderTimeMain}>
                  {selectedEndTime.toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit', hour12: true
                  })}
                </Text>
              </View>
            </View>
          ) : undefined}
          headerBackgroundColor={calendar?.color || calendarConfig?.color || colors.primary}
          size="medium"
          scrollable={true}
        footer={
          <View style={[styles.modalActions, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={styles.actionBtn}
              onPress={() => setShowTimeModal(false)}
              activeOpacity={0.7}
            >
              <Text style={styles.actionBtnText}>{t('calendar.booking.cancelButton')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionBtn, styles.actionBtnPrimary, booking && styles.actionBtnDisabled]}
              onPress={handleBooking}
              disabled={booking || !selectedStartTime || !selectedEndTime}
              activeOpacity={0.8}
            >
              {booking ? (
                <ActivityIndicator color={colors.white} size="small" />
              ) : (
                <Text style={styles.actionBtnPrimaryText}>{t('calendar.booking.bookButton')}</Text>
              )}
            </TouchableOpacity>
          </View>
        }
      >
        {/* Iconized button to view existing events */}
        {selectedDay && getDateEvents(selectedDay).length > 0 && (
          <TouchableOpacity
            style={styles.modalSwitchButton}
            onPress={() => {
              setShowTimeModal(false);
              setShowEventSelectModal(true);
            }}
            activeOpacity={0.7}
          >
            <View style={[styles.modalSwitchButtonContent, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.modalSwitchButtonIcon, rtl.marginEnd(spacing.md)]}>
                <Ionicons name="calendar-outline" size={24} color={colors.primary} />
                <View style={styles.modalSwitchButtonBadge}>
                  <Text style={styles.modalSwitchButtonBadgeText}>
                    {getDateEvents(selectedDay).length}
                  </Text>
                </View>
              </View>
              <View style={[styles.modalSwitchButtonText, rtl.marginEnd(spacing.sm)]}>
                <Text style={styles.modalSwitchButtonTitle}>
                  {t('calendar.booking.viewExistingAppointments')}
                </Text>
                <Text style={styles.modalSwitchButtonSubtitle}>
                  {t('calendar.booking.appointmentsOnDay', {
                    count: getDateEvents(selectedDay).length,
                    label: t(getDateEvents(selectedDay).length === 1 ? 'calendar.booking.appointmentSingular' : 'calendar.booking.appointmentPlural'),
                  })}
                </Text>
              </View>
              <DirectionalIcon name="chevron-forward" size={20} color={colors.text.secondary} />
            </View>
          </TouchableOpacity>
        )}

        {/* Time selection - improved picker */}
        <View style={styles.timeBox}>
          {/* Time picker - side by side */}
          <View style={[styles.timePickerRow, { flexDirection: rtl.rowDirection }]}>
            {/* Hour picker - text with arrows */}
            <View style={styles.timePickerColumn}>
              <Text style={styles.timePickerTitle}>{t('calendar.booking.hourLabel')}</Text>
              <View style={styles.timePickerTextContainer}>
                <TouchableOpacity
                  style={styles.timePickerArrow}
                  onPress={() => {
                    const availableHours = getAvailableHours();
                    if (availableHours.length === 0) return;
                    const currentIndex = availableHours.findIndex(h => h.hour === selectedHour && h.ampm === selectedAmPm);
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : availableHours.length - 1;
                    const newHour = availableHours[prevIndex];
                    setSelectedHour(newHour.hour);
                    setSelectedAmPm(newHour.ampm);
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chevron-up" size={20} color={colors.text.primary} />
                </TouchableOpacity>
                <Text style={styles.timePickerTextValue}>
                  {selectedHour}
                </Text>
                <TouchableOpacity
                  style={styles.timePickerArrow}
                  onPress={() => {
                    const availableHours = getAvailableHours();
                    if (availableHours.length === 0) return;
                    const currentIndex = availableHours.findIndex(h => h.hour === selectedHour && h.ampm === selectedAmPm);
                    const nextIndex = currentIndex < availableHours.length - 1 ? currentIndex + 1 : 0;
                    const newHour = availableHours[nextIndex];
                    setSelectedHour(newHour.hour);
                    setSelectedAmPm(newHour.ampm);
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chevron-down" size={20} color={colors.text.primary} />
                </TouchableOpacity>
              </View>
            </View>
            
            <Text style={styles.timePickerSeparator}>:</Text>
            
            {/* Minute picker - text with arrows */}
            <View style={styles.timePickerColumn}>
              <Text style={styles.timePickerTitle}>{t('calendar.booking.minuteLabel')}</Text>
              <View style={styles.timePickerTextContainer}>
                <TouchableOpacity
                  style={styles.timePickerArrow}
                  onPress={() => {
                    const minutes = [0, 15, 30, 45];
                    const currentIndex = minutes.indexOf(selectedMinute);
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : minutes.length - 1;
                    setSelectedMinute(minutes[prevIndex]);
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chevron-up" size={20} color={colors.text.primary} />
                </TouchableOpacity>
                <Text style={styles.timePickerTextValue}>
                  {selectedMinute.toString().padStart(2, '0')}
                </Text>
                <TouchableOpacity
                  style={styles.timePickerArrow}
                  onPress={() => {
                    const minutes = [0, 15, 30, 45];
                    const currentIndex = minutes.indexOf(selectedMinute);
                    const nextIndex = currentIndex < minutes.length - 1 ? currentIndex + 1 : 0;
                    setSelectedMinute(minutes[nextIndex]);
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chevron-down" size={20} color={colors.text.primary} />
                </TouchableOpacity>
              </View>
            </View>
            
            {/* AM/PM picker - text with arrows - only show if both AM and PM hours are available */}
            {(() => {
              const availableHours = getAvailableHours();
              const hasAM = availableHours.some(h => h.ampm === 'AM');
              const hasPM = availableHours.some(h => h.ampm === 'PM');
              
              // Only show AM/PM picker if both AM and PM hours are available
              if (hasAM && hasPM) {
                return (
                  <View style={styles.timePickerColumn}>
                    <Text style={styles.timePickerTitle}>{t('calendar.booking.periodLabel')}</Text>
                    <View style={styles.timePickerTextContainer}>
                      <TouchableOpacity
                        style={styles.timePickerArrow}
                        onPress={() => {
                          const newAmPm = selectedAmPm === 'AM' ? 'PM' : 'AM';
                          // Check if the current hour with new AM/PM is available
                          const isAvailable = availableHours.some(h => h.hour === selectedHour && h.ampm === newAmPm);
                          if (isAvailable) {
                            setSelectedAmPm(newAmPm);
                          } else {
                            // Find the closest available hour with the new AM/PM
                            const availableWithNewAmPm = availableHours.filter(h => h.ampm === newAmPm);
                            if (availableWithNewAmPm.length > 0) {
                              const closest = availableWithNewAmPm[0];
                              setSelectedHour(closest.hour);
                              setSelectedAmPm(closest.ampm);
                            }
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <Ionicons name="chevron-up" size={20} color={colors.text.primary} />
                      </TouchableOpacity>
                      <Text style={styles.timePickerTextValue}>
                        {selectedAmPm}
                      </Text>
                      <TouchableOpacity
                        style={styles.timePickerArrow}
                        onPress={() => {
                          const newAmPm = selectedAmPm === 'AM' ? 'PM' : 'AM';
                          // Check if the current hour with new AM/PM is available
                          const isAvailable = availableHours.some(h => h.hour === selectedHour && h.ampm === newAmPm);
                          if (isAvailable) {
                            setSelectedAmPm(newAmPm);
                          } else {
                            // Find the closest available hour with the new AM/PM
                            const availableWithNewAmPm = availableHours.filter(h => h.ampm === newAmPm);
                            if (availableWithNewAmPm.length > 0) {
                              const closest = availableWithNewAmPm[0];
                              setSelectedHour(closest.hour);
                              setSelectedAmPm(closest.ampm);
                            }
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <Ionicons name="chevron-down" size={20} color={colors.text.primary} />
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              }
              return null;
            })()}
          </View>
          
          {/* Duration - improved */}
          <View style={styles.durationSection}>
            <Text style={styles.durationSectionTitle}>{t('calendar.booking.durationLabel')}</Text>
            <View style={[styles.durationGrid, { flexDirection: rtl.rowDirection }]}>
              {[30, 60, 90, 120].map(mins => (
                <TouchableOpacity
                  key={mins}
                  style={[styles.durationChip, duration === mins && styles.durationChipActive]}
                  onPress={() => setDuration(mins)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.durationChipText, duration === mins && styles.durationChipTextActive]}>
                    {t('calendar.booking.durationMinutes', { minutes: mins })}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Additional fields */}
          <View style={styles.additionalFields}>
            {/* Notes */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{t('calendar.booking.notesLabel')}</Text>
              <TextInput
                style={styles.textInput}
                value={notes}
                onChangeText={setNotes}
                placeholder={t('calendar.booking.notesPlaceholder')}
                placeholderTextColor={colors.text.secondary}
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Join URL */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{t('calendar.booking.joinUrlLabel')}</Text>
              <TextInput
                style={styles.textInput}
                value={joinUrl}
                onChangeText={setJoinUrl}
                placeholder={t('calendar.booking.joinUrlPlaceholder')}
                placeholderTextColor={colors.text.secondary}
                keyboardType="url"
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>

            {/* Attachments */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{t('calendar.booking.attachmentsLabel')}</Text>
              <TouchableOpacity
                style={[styles.uploadButton, { flexDirection: rtl.rowDirection }]}
                onPress={async () => {
                  try {
                    const result = await MediaService.pickDocument();
                    if (result) {
                      setAttachments([...attachments, {
                        uri: result.uri,
                        name: result.fileName,
                        type: result.mimeType || 'application/octet-stream',
                      }]);
                    }
                  } catch (error: any) {
                    showNotification('error', t('common.error'), error.message || t('calendar.booking.pickFileFailedError'));
                  }
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="attach" size={20} color={colors.primary} />
                <Text style={styles.uploadButtonText}>{t('calendar.booking.addFileButton')}</Text>
              </TouchableOpacity>
              {attachments.length > 0 && (
                <View style={styles.attachmentsList}>
                  {attachments.map((file, index) => (
                    <View key={index} style={[styles.attachmentItem, { flexDirection: rtl.rowDirection }]}>
                      <Ionicons name="document-text" size={16} color={colors.text.secondary} />
                      <Text style={styles.attachmentName} numberOfLines={1}>{file.name}</Text>
        <TouchableOpacity
                        style={styles.removeAttachment}
                        onPress={() => {
                          setAttachments(attachments.filter((_, i) => i !== index));
                        }}
                        activeOpacity={0.7}
                      >
                        <Ionicons name="close-circle" size={18} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        </View>
        </Modal>

        {/* Event Selection Modal - For selecting events when more than 2 */}
        <Modal
          visible={showEventSelectModal}
          onClose={() => setShowEventSelectModal(false)}
          title={selectedDay ? selectedDay.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }) : t('calendar.booking.selectAppointmentTitle')}
          size="medium"
          scrollable={true}
        >
          {/* Iconized button to create new event */}
          {selectedDay && (
            <TouchableOpacity
              style={styles.modalSwitchButton}
              onPress={() => {
                setShowEventSelectModal(false);
                setShowTimeModal(true);
              }}
              activeOpacity={0.7}
            >
              <View style={[styles.modalSwitchButtonContent, { flexDirection: rtl.rowDirection }]}>
                <View style={[styles.modalSwitchButtonIcon, rtl.marginEnd(spacing.md)]}>
                  <Ionicons name="add-circle-outline" size={24} color={colors.primary} />
                </View>
                <View style={[styles.modalSwitchButtonText, rtl.marginEnd(spacing.sm)]}>
                  <Text style={styles.modalSwitchButtonTitle}>
                    {t('calendar.booking.createNewAppointment')}
                  </Text>
                  <Text style={styles.modalSwitchButtonSubtitle}>
                    {t('calendar.booking.addNewAppointmentForDay')}
                  </Text>
                </View>
                <DirectionalIcon name="chevron-forward" size={20} color={colors.text.secondary} />
              </View>
            </TouchableOpacity>
          )}

          {selectedDay && getDateEvents(selectedDay).map((event, i) => {
            const calendar = event.calendarId ? calendarsMap.get(event.calendarId) : null;
            const calendarColor = calendar?.color || colors.primary;
            const calendarName = calendar?.name || t('calendar.booking.calendarFallback');

            return (
              <TouchableOpacity
                key={event._id || i}
                style={[styles.existingEventCard, { borderLeftColor: calendarColor, marginBottom: spacing.sm }]}
                onPress={() => {
                  setSelectedEventForEdit(event);
                  setShowEventSelectModal(false);
                  setShowEventEditModal(true);
                }}
                activeOpacity={0.7}
              >
                <View style={styles.existingEventContent}>
                  <View style={styles.existingEventHeader}>
                    <View style={[styles.existingEventTag, { backgroundColor: calendarColor + '20', flexDirection: rtl.rowDirection }]}>
                      <View style={[styles.existingEventTagDot, rtl.marginEnd(spacing.xs / 2), { backgroundColor: calendarColor }]} />
                      <Text style={[styles.existingEventTagText, { color: calendarColor }]}>
                        {calendarName}
                      </Text>
                    </View>
                  </View>
                  <View style={[styles.existingEventTime, { flexDirection: rtl.rowDirection }]}>
                    <Text style={[styles.existingEventTimeText, { color: calendarColor }]}>
                      {new Date(event.startDate).toLocaleTimeString('en-US', {
                        hour: 'numeric', minute: '2-digit', hour12: true
                      })}
                    </Text>
                    {event.endDate && (
                      <Text style={styles.existingEventTimeTo}>
                        {' - '}
                        {new Date(event.endDate).toLocaleTimeString('en-US', {
                          hour: 'numeric', minute: '2-digit', hour12: true
                        })}
                      </Text>
                    )}
                  </View>
                  <Text style={styles.existingEventTitle} numberOfLines={2}>
                    {event.title || t('calendar.booking.untitledEvent')}
                  </Text>
                  {event.description && (
                    <Text style={styles.existingEventDescription} numberOfLines={1}>
                      {event.description}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </Modal>

        {/* Event Edit Modal - For viewing/editing selected event */}
        <Modal
          visible={showEventEditModal}
          onClose={() => {
            setShowEventEditModal(false);
            setSelectedEventForEdit(null);
          }}
          title={selectedEventForEdit?.title || t('calendar.booking.eventDetailsTitle')}
          size="large"
          scrollable={true}
          customHeader={selectedEventForEdit ? (
            <View style={[styles.eventHeaderDateTime, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="time" size={20} color={colors.white} />
              <View style={[styles.eventHeaderDateTimeText, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.eventHeaderTimeMain}>
                  {new Date(selectedEventForEdit.startDate).toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit', hour12: true
                  })}
                </Text>
                <Text style={styles.eventHeaderTimeTo}>{t('calendar.booking.timeRangeTo')}</Text>
                <Text style={styles.eventHeaderTimeMain}>
                  {new Date(selectedEventForEdit.endDate).toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit', hour12: true
                  })}
                </Text>
              </View>
            </View>
          ) : undefined}
          headerBackgroundColor={selectedEventForEdit ? (() => {
            const calendar = selectedEventForEdit.calendarId ? calendarsMap.get(selectedEventForEdit.calendarId) : null;
            return calendar?.color || colors.primary;
          })() : undefined}
          footer={
            <View style={[styles.modalActions, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={styles.actionBtn}
                onPress={() => {
                  setShowEventEditModal(false);
                  setSelectedEventForEdit(null);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.actionBtnText}>{t('common.close')}</Text>
              </TouchableOpacity>
            </View>
          }
        >
          {selectedEventForEdit && (
            <View style={styles.eventEditContainer}>

              {selectedEventForEdit.title && (
                <View style={styles.eventEditSection}>
                  <Text style={styles.eventEditLabel}>{t('calendar.booking.titleLabel')}</Text>
                  <Text style={styles.eventEditValue}>{selectedEventForEdit.title}</Text>
                </View>
              )}

              {selectedEventForEdit.description && (
                <View style={styles.eventEditSection}>
                  <Text style={styles.eventEditLabel}>{t('calendar.booking.descriptionLabel')}</Text>
                  <Text style={styles.eventEditValue}>{selectedEventForEdit.description}</Text>
                </View>
              )}

              {selectedEventForEdit.location && (
                <View style={styles.eventEditSection}>
                  <Text style={styles.eventEditLabel}>{t('calendar.booking.locationLabel')}</Text>
                  <Text style={styles.eventEditValue}>{selectedEventForEdit.location}</Text>
                </View>
              )}

              {selectedEventForEdit.onlineMeeting?.joinUrl && (
                <View style={styles.eventEditSection}>
                  <Text style={styles.eventEditLabel}>{t('calendar.booking.meetingLinkLabel')}</Text>
                  <Text style={[styles.eventEditValue, styles.eventEditLink]}>
                    {selectedEventForEdit.onlineMeeting.joinUrl}
                  </Text>
                </View>
              )}

              {(() => {
                // Check for attachments in various possible locations/structures
                const attachments = selectedEventForEdit.attachments || 
                                  selectedEventForEdit.attachment || 
                                  (Array.isArray(selectedEventForEdit.attachment) ? selectedEventForEdit.attachment : []);
                
                // Debug log
                console.log('[EventEditModal] Checking attachments:', {
                  hasAttachments: !!selectedEventForEdit.attachments,
                  attachmentsCount: selectedEventForEdit.attachments?.length || 0,
                  attachments: selectedEventForEdit.attachments,
                  processedAttachments: attachments,
                  processedCount: attachments?.length || 0
                });
                
                if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
                  return null;
                }
                
                return (
                  <View style={styles.eventEditSection}>
                    <Text style={styles.eventEditLabel}>{t('calendar.booking.attachmentsWithCount', { count: attachments.length })}</Text>
                    {attachments.map((attachment: any, index: number) => {
                    // Support both entity structure (url, name, type) and alternative structures
                    const fileUrl = attachment.url || attachment.fileUrl || attachment.uri;
                    const fileName = attachment.name || attachment.fileName;
                    const mimeType = attachment.type || attachment.mimeType;
                    const fileId = attachment._id || attachment.fileId || index;

                    return (
                      <TouchableOpacity
                        key={fileId}
                        style={[styles.eventAttachmentItem, { flexDirection: rtl.rowDirection }]}
                        onPress={async () => {
                          if (fileUrl) {
                            let urlToOpen = fileUrl;
                            
                            // If it's a Firebase Storage URL, use the proxy endpoint to avoid permission issues
                            if (fileUrl.includes('firebasestorage.googleapis.com')) {
                              try {
                                const { getCurrentApiConfig } = await import('../config/api.config');
                                const apiConfig = getCurrentApiConfig();
                                urlToOpen = `${apiConfig.baseURL}/api/storage/media/proxy?url=${encodeURIComponent(fileUrl)}`;
                              } catch (error) {
                                console.error('Failed to get API config for proxy URL:', error);
                                // Fallback to original URL
                              }
                            }
                            
                            if (Platform.OS === 'web') {
                              window.open(urlToOpen, '_blank');
                            } else {
                              Linking.openURL(urlToOpen);
                            }
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <Ionicons 
                          name={mimeType?.startsWith('image/') ? 'image-outline' : 
                                mimeType?.startsWith('video/') ? 'videocam-outline' :
                                mimeType?.includes('pdf') ? 'document-text-outline' :
                                'document-outline'} 
                          size={20} 
                          color={colors.primary} 
                        />
                        <Text style={styles.eventAttachmentName} numberOfLines={1}>
                          {fileName || t('calendar.booking.attachmentFallback', { number: index + 1 })}
                        </Text>
                        {fileUrl && (
                          <Ionicons name="open-outline" size={16} color={colors.text.secondary} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                  </View>
                );
              })()}
            </View>
          )}
        </Modal>

        {/* Floating Calendar Picker Modal */}
        <Modal
          visible={showCalendarPicker}
          onClose={() => setShowCalendarPicker(false)}
          title={t('calendar.booking.selectDateTitle')}
          size="medium"
        >
          <View style={{ padding: spacing.md }}>
            <CalendarMonthView
              currentDate={calendarPickerDate}
              selectedDate={selectedDay || undefined}
              events={[]}
              onDateSelect={(date: Date) => {
                setSelectedDay(date);
                setViewDate(date);
                setShowCalendarPicker(false);
              }}
              onPreviousMonth={() => {
                const prev = new Date(calendarPickerDate);
                prev.setMonth(prev.getMonth() - 1);
                setCalendarPickerDate(prev);
              }}
              onNextMonth={() => {
                const next = new Date(calendarPickerDate);
                next.setMonth(next.getMonth() + 1);
                setCalendarPickerDate(next);
              }}
            />
          </View>
        </Modal>

        {/* Success Modal */}
        {showSuccessModal && (
          <View style={styles.successModalOverlay}>
            <View style={styles.successModalContainer}>
              <TouchableOpacity
                style={styles.successCloseBtn}
                onPress={handleCloseSuccess}
                activeOpacity={0.7}
              >
                <Ionicons name="close" size={24} color={colors.text.secondary} />
              </TouchableOpacity>
              
              <View style={styles.successCheckmarkContainer}>
                <View style={styles.successCheckmark} />
              </View>
              
              <Text style={styles.successCountdown}>{countdown}</Text>
              <Text style={styles.successAutoCloseText}>{t('calendar.booking.autoCloseText')}</Text>
            </View>
          </View>
        )}
      </View>
    );
  };

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.md,
    ...(Platform.OS === 'web' && {
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
    } as any),
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeBtn: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: spacing.borderRadius.full,
    backgroundColor: colors.background.secondary,
    zIndex: 10,
  },
  // Calendar container - fills screen better (85% width)
  calendarContainer: {
    maxWidth: '100%',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.sm,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    alignSelf: 'center', // Center the container
  },
  monthNav: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  navBtn: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  monthLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  viewModeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
  },
  // Day view styles
  dayViewContainer: {
    flex: 1,
    padding: spacing.md,
  },
  dayViewHeader: {
    marginBottom: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  dayViewDate: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  dayViewDuration: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  noSlotsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
  },
  noSlotsText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.md,
  },
  slotsContainer: {
    gap: spacing.sm,
  },
  timeSlot: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  timeSlotBooked: {
    backgroundColor: colors.background.secondary,
    opacity: 0.7,
  },
  timeSlotContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  timeSlotTime: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 120,
  },
  timeSlotTimeText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  timeSlotTimeTextBooked: {
    color: colors.text.secondary,
    textDecorationLine: 'line-through',
  },
  timeSlotTimeSeparator: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginHorizontal: spacing.xs,
  },
  timeSlotEvent: {
    flex: 1,
    marginLeft: spacing.md,
  },
  timeSlotEventTag: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.sm,
    marginBottom: spacing.xs,
  },
  timeSlotEventTagDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: spacing.xs,
  },
  timeSlotEventTagText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  timeSlotEventTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
  },
  timeSlotEventDescription: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  timeSlotAvailable: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginLeft: spacing.md,
  },
  timeSlotAvailableText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  monthGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    // Fixed: Ensure exactly 7 columns by using percentage widths
  },
  weekDayHeader: {
    width: '14.28%', // Fixed: 100% / 7 = 14.28%
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  weekDayText: {
    fontSize: typography.fontSize.xs - 2,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
  },
  dayCell: {
    width: '14.28%', // Fixed: 100% / 7 = 14.28%
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: 'transparent',
    position: 'relative',
    marginBottom: 2,
    minHeight: 40, // Ensure minimum size
    padding: 3,
    overflow: 'hidden',
  },
  dayCellNoEvents: {
    justifyContent: 'center', // Center when no events
    alignItems: 'center',
  },
  dayCellHeader: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 2,
    paddingBottom: 2,
  },
  dayCellHeaderCentered: {
    flex: 1, // Take full height when no events
    justifyContent: 'center',
  },
  dayCellEmpty: {
    width: '14.28%', // Fixed: 100% / 7 = 14.28%
    marginBottom: 2,
    // Height will be set dynamically via inline style to match dayCell
  },
  dayToday: {
    backgroundColor: colors.primary + '12',
    borderColor: colors.primary,
  },
  daySelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 2,
    elevation: 2,
  },
  dayPast: {
    opacity: 0.6,
  },
  dayCellUnavailable: {
    opacity: 0.4,
    backgroundColor: colors.background.secondary,
  },
  dayNumber: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  dayNumberToday: {
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  dayNumberSelected: {
    color: colors.white,
    fontFamily: typography.fontFamily.bold,
  },
  dayNumberPast: {
    color: colors.text.secondary,
  },
  dayNumberUnavailable: {
    color: colors.text.secondary,
    opacity: 0.5,
  },
  dayEventsContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: 3,
    marginTop: 3,
    paddingHorizontal: 2,
  },
  dayEventCard: {
    width: '100%',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderLeftWidth: 3,
    borderColor: colors.border,
    paddingHorizontal: 4,
    paddingVertical: 3,
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 22,
  },
  dayEventTag: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayEventTagDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dayEventTime: {
    fontSize: typography.fontSize.xs - 2,
    fontFamily: typography.fontFamily.medium,
    lineHeight: typography.fontSize.xs,
    flex: 1,
  },
  dayEventMore: {
    width: '100%',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    paddingVertical: 3,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 20,
  },
  dayEventMoreText: {
    fontSize: typography.fontSize.xs - 2,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  dayEventCount: {
    fontSize: typography.fontSize.xs - 4,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
    marginTop: 1,
  },
  dayEventCountSelected: {
    color: colors.white,
  },
  existingBox: {
    marginBottom: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  existingLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Modal switch button styles
  modalSwitchButton: {
    width: '100%',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.md,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
      default: {
        elevation: 1,
      },
    }),
  },
  modalSwitchButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
  },
  modalSwitchButtonIcon: {
    width: 48,
    height: 48,
    borderRadius: spacing.borderRadius.full,
    backgroundColor: colors.primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
    position: 'relative',
  },
  modalSwitchButtonBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: colors.primary,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: colors.white,
  },
  modalSwitchButtonBadgeText: {
    fontSize: typography.fontSize.xs - 4,
    fontFamily: typography.fontFamily.bold,
    color: colors.white,
  },
  modalSwitchButtonText: {
    flex: 1,
    marginRight: spacing.sm,
  },
  modalSwitchButtonTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
  },
  modalSwitchButtonSubtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  existingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs / 2,
  },
  existingDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: colors.primary,
    marginRight: spacing.xs,
  },
  existingTime: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginRight: spacing.xs,
    minWidth: 55,
  },
  existingTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    flex: 1,
  },
  // Enhanced event cards
  existingEventCard: {
    width: '100%',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderLeftWidth: 4,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
      default: {
        elevation: 1,
      },
    }),
  },
  existingEventContent: {
    flex: 1,
  },
  existingEventHeader: {
    marginBottom: spacing.xs,
  },
  existingEventTag: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.full,
  },
  existingEventTagDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: spacing.xs / 2,
  },
  existingEventTagText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  existingEventTime: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  existingEventTimeText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  existingEventTimeTo: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  existingEventTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
  },
  existingEventDescription: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  existingEventMore: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginTop: spacing.xs,
  },
  existingEventMoreText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  // Event edit modal styles
  eventEditContainer: {
    paddingVertical: spacing.md,
  },
  eventEditSection: {
    marginBottom: spacing.md,
  },
  eventEditLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  eventEditValue: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    lineHeight: 20,
  },
  eventHeaderDateTime: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: spacing.sm,
  },
  eventHeaderDateTimeText: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  eventHeaderTimeMain: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.white,
  },
  eventHeaderTimeTo: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.white + 'CC',
  },
  eventEditLink: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  eventAttachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    marginTop: spacing.xs,
    gap: spacing.sm,
  },
  eventAttachmentName: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
  },
  timeBox: {
    // Content area
  },
  timeDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.lg,
    backgroundColor: colors.primary + '08',
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary + '20',
  },
  timeDisplayText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: spacing.sm,
    gap: spacing.sm,
  },
  timeDisplayMain: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
  },
  timeDisplayTo: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  timePickerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: spacing.lg,
    gap: spacing.md,
  },
  timePickerColumn: {
    flex: 1,
    alignItems: 'center',
  },
  timePickerTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  timePickerContainer: {
    width: '100%',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.xs,
    minHeight: 180,
    justifyContent: 'center',
  },
  timePickerTextContainer: {
    width: '100%',
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  timePickerArrow: {
    padding: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timePickerTextValue: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginVertical: spacing.sm,
    minHeight: 32,
    textAlign: 'center',
  },
  timePickerOption: {
    width: '100%',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.xs / 2,
  },
  timePickerOptionActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 2,
  },
  timePickerOptionText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  timePickerOptionTextActive: {
    color: colors.white,
  },
  timePickerSeparator: {
    fontSize: typography.fontSize.xxl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginTop: spacing.xl + spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  durationSection: {
    marginTop: spacing.sm,
  },
  durationSectionTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  durationGrid: {
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'center',
  },
  durationChip: {
    flex: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: spacing.borderRadius.full,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  durationChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  durationChipText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  durationChipTextActive: {
    color: colors.white,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  actionBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: spacing.borderRadius.md,
  },
  actionBtnPrimary: {
    backgroundColor: colors.primary,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnDisabled: {
    opacity: 0.5,
  },
  actionBtnText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  actionBtnPrimaryText: {
    color: colors.white,
    fontFamily: typography.fontFamily.bold,
  },
  additionalFields: {
    marginTop: spacing.lg,
    paddingTop: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  fieldContainer: {
    marginBottom: spacing.md,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  textInput: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    minHeight: 44,
    textAlignVertical: 'top',
  },
  fieldHint: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.xs / 2,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    gap: spacing.xs,
  },
  uploadButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  attachmentsList: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    gap: spacing.xs,
  },
  attachmentName: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
  },
  removeAttachment: {
    padding: spacing.xs / 2,
  },
  successModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  successModalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: 20,
    padding: spacing.xl,
    alignItems: 'center',
    width: '90%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 10,
  },
  successCloseBtn: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    padding: spacing.xs,
    zIndex: 1,
  },
  successCheckmarkContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  successCheckmark: {
    width: 50,
    height: 50,
    borderLeftWidth: 4,
    borderBottomWidth: 4,
    borderColor: colors.white,
    transform: [{ rotate: '-45deg' }],
    marginLeft: -8,
    marginTop: -12,
  },
  successCountdown: {
    fontSize: 48,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
    marginTop: spacing.md,
  },
  successAutoCloseText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.sm,
  },
});
